import axios from 'axios';
import { Store } from 'vuex';
import VueRouter from 'vue-router';
import TranslationService from '@/locale/translation.service';
import buildPaginationQueryOpts from '@/shared/sort/sorts';

export default class AccountService {
  constructor(private store: Store<any>, private translationService: TranslationService, private router: VueRouter) {
    this.init();
  }

  public isFetching = false;

  public init(): void {
    this.retrieveProfiles();
  }

  public retrieveProfiles(): Promise<boolean> {
    return new Promise(resolve => {
      axios
        .get('management/info')
        .then(res => {
          if (res.data && res.data.activeProfiles) {
            this.store.commit('setRibbonOnProfiles', res.data['display-ribbon-on-profiles']);
            this.store.commit('setActiveProfiles', res.data['activeProfiles']);
          }
          resolve(true);
        })
        .catch(() => resolve(false));
    });
  }

  public retrieveAccount(doLogout?: boolean): Promise<boolean> {
    return new Promise(resolve => {
      axios
        .get('api/v1/account')
        .then(response => {
          this.store.commit('authenticate');
          const account = response.data;
          if (account) {
            this.store.commit('authenticated', account);
            if (this.store.getters.currentLanguage !== account.langKey) {
              this.store.commit('currentLanguage', account.langKey);
            }
          } else {
            this.logout();
          }
          this.translationService.refreshTranslation(this.store.getters.currentLanguage);
          resolve(true);
        })
        .catch(err => {
          const status = err.status || err.response.status;
          if (status === 403 || status === 401) {
            if (doLogout) {
              this.logout();
            }
          }
          resolve(false);
        });
    });
  }

  public logout() {
    this.store.commit('logout');
    localStorage.removeItem('jhi-authenticationToken');
    sessionStorage.removeItem('jhi-authenticationToken');
    sessionStorage.removeItem('requested-url');
    this.router.push('/', () => {});
  }

  public hasAnyAuthorityAndCheckAuth(authorities: any): Promise<boolean> {
    if (!this.authenticated || !this.userAuthorities) {
      const token = localStorage.getItem('jhi-authenticationToken') || sessionStorage.getItem('jhi-authenticationToken');
      if (!this.store.getters.account && !this.store.getters.logon && token) {
        return this.retrieveAccount();
      } else {
        return new Promise(resolve => {
          resolve(false);
        });
      }
    }

    if (this.hasAnyAuthority(authorities)) {
      return new Promise(resolve => {
        resolve(true);
      });
    }

    return new Promise(resolve => {
      resolve(false);
    });
  }

  public hasAnyAuthority(authorities: any) {
    if (typeof authorities === 'string') {
      authorities = [authorities];
    }
    const activeCompany = this.userCompany.find(company => company.company.id === this.activeEntity?.company.id);
    const userAuthorities = [this.activeEntity && this.activeEntity.role ? this.activeEntity.role : 'EMPLOYEE', ...this.userAuthorities];
    if (activeCompany && activeCompany.role === 'MANAGER') {
      userAuthorities.push('COMPANY_ACCESS');
    } else {
      userAuthorities.push('SHOP_ACCESS');
    }

    const matched = userAuthorities.filter(authority => authorities.includes(authority));
    if (this.authenticated && !this.userAuthorities.some(auth => auth.includes('ROLE_ADMIN'))) {
      return matched.length >= 3;
    }
    return !!this.userAuthorities?.find(authority => authorities.includes(authority));
  }

  public isAdmin() {
    this.hasAnyAuthorityAndCheckAuth('ROLES_ADMIN');
  }

  public get authenticated(): boolean {
    return this.store.getters.authenticated;
  }

  public get userAuthorities(): any {
    return this.store.getters.account?.authorities;
  }

  public get userCompany(): any {
    return this.store.getters.account.userCompanies;
  }

  public get activeEntity(): any {
    return this.store.getters.activeEntity;
  }
}
